import { convertToCommonErrors, convertToCommonErrorsWithMessage } from '../../common/utils/errorUtils.js';
import { TypeKeys, generateActionId } from './actionsUtils.js';
import type { CrudAction, ErrorAction, ErrorActionCreator } from './actionsUtils.js';
import type { WizardType } from '../../common/enums.js';
import type { ConfiguredOffer } from '../../common/types/commercialProduct.js';
import type { DeviceChangeOrRedeemRequest, DeviceChangeRequest } from '../../common/types/device.js';
import type { CommonError } from '../../common/types/errors.js';
import type { UpsertPersonBillingAccountRequest } from '../../common/utils/billingAccountUtils.js';
import type {
  AddOn,
  Address,
  CompanyInfoResponse,
  ContactPerson,
  CreditCheckResponse,
  DeliveryAddress,
  DeliveryType,
  GenericError,
  OnlineOrder,
  PutBillingAccountResponse,
} from '../../generated/api/models.js';
import type { Action } from 'redux';
import type { AjaxResponse } from 'rxjs/ajax';

export interface SubmitOnlineOrderAction extends CrudAction<TypeKeys.SUBMIT_ONLINE_ORDER> {
  addOns?: AddOn[];
  approverContact?: string;
  approverContactValidationErrors?: CommonError[];
  contactPerson?: ContactPerson;
  contactPersonValidationErrors?: CommonError[];
  customerType?: CompanyInfoResponse.CustomerTypeEnum;
  deliveryAddress: DeliveryAddress;
  isCompanyNameRequired?: boolean;
  deliveryMethodType?: DeliveryType;
  billingAccountId?: string;
  items: ConfiguredOffer[];
  overridePreviousAction?: boolean;
  personBillingAccountId?: string;
  personBillingAddress?: Address;
  deviceChangeRequest?: DeviceChangeOrRedeemRequest;
  shipmentType?: OnlineOrder.ShipmentTypeEnum;
  personBillingEmail?: string;
  wizardType?: WizardType;
  voucherCode?: string;
}

export const submitOnlineOrder = (
  orderItems: ConfiguredOffer[],
  deliveryAddress: DeliveryAddress,
  contactPerson?: ContactPerson,
  contactPersonValidationErrors?: CommonError[],
  customerType?: CompanyInfoResponse.CustomerTypeEnum,
  isCompanyNameRequired?: boolean,
  billingAccountId?: string,
  addOns?: AddOn[],
  overridePreviousAction?: boolean,
  deliveryMethodType?: DeliveryType,
  approverContact?: string,
  approverContactValidationErrors?: CommonError[],
  personBillingAccountId?: string,
  personBillingEmail?: string,
  personBillingAddress?: Address,
  deviceChangeRequest?: DeviceChangeRequest,
  shipmentType?: OnlineOrder.ShipmentTypeEnum,
  voucherCode?: string
): SubmitOnlineOrderAction => ({
  addOns,
  approverContact,
  approverContactValidationErrors,
  billingAccountId,
  deliveryAddress,
  contactPerson,
  contactPersonValidationErrors,
  customerType,
  isCompanyNameRequired,
  deliveryMethodType,
  id: generateActionId(),
  items: orderItems,
  overridePreviousAction,
  personBillingAccountId,
  personBillingEmail,
  personBillingAddress,
  deviceChangeRequest,
  shipmentType,
  type: TypeKeys.SUBMIT_ONLINE_ORDER,
  voucherCode,
});

export interface SubmitOrderFulfilledAction extends Action {
  orderId: string;
  type: TypeKeys.SUBMIT_ONLINE_ORDER_FULFILLED;
}

export const submitOnlineOrderFulfilled = (response: AjaxResponse): SubmitOrderFulfilledAction => {
  return {
    orderId: response.response.onlineId ? response.response.onlineId : response.response.onlineRequestId, // V2 stack of online-order, returns onlineRequestId in response.
    type: TypeKeys.SUBMIT_ONLINE_ORDER_FULFILLED,
  };
};

export type SubmitOrderFailedAction = ErrorAction<TypeKeys.SUBMIT_ONLINE_ORDER_FAILED>;

export const submitOnlineOrderFailed: ErrorActionCreator<TypeKeys.SUBMIT_ONLINE_ORDER_FAILED> = (
  message: string,
  status: number,
  errors?: GenericError[]
): SubmitOrderFailedAction => {
  const commonErrors = convertToCommonErrorsWithMessage(message, status, errors);
  return {
    errors: commonErrors,
    type: TypeKeys.SUBMIT_ONLINE_ORDER_FAILED,
  };
};

export interface PollForCreditCheckAction extends CrudAction<TypeKeys.POLL_FOR_CREDIT_CHECK> {
  orderId: string;
}

export const pollForCreditCheck = (orderId: string): PollForCreditCheckAction => ({
  id: generateActionId(),
  orderId: orderId,
  type: TypeKeys.POLL_FOR_CREDIT_CHECK,
});

export interface PollForCreditCheckV2FulfilledAction extends Action {
  onlineOrderId: string;
  response: CreditCheckResponse;
  type: TypeKeys.POLL_FOR_CREDIT_CHECK_V2_FULFILLED;
}

export const pollForCreditCheckV2Fulfilled = (
  response: CreditCheckResponse,
  onlineOrderId: string
): PollForCreditCheckV2FulfilledAction => ({
  onlineOrderId,
  response,
  type: TypeKeys.POLL_FOR_CREDIT_CHECK_V2_FULFILLED,
});

export type PollForCreditCheckFailedAction = ErrorAction<TypeKeys.POLL_FOR_CREDIT_CHECK_FAILED>;

export const pollForCreditCheckFailed: ErrorActionCreator<TypeKeys.POLL_FOR_CREDIT_CHECK_FAILED> = (
  message: string,
  status: number,
  errors?: GenericError[]
): PollForCreditCheckFailedAction => {
  const commonErrors = convertToCommonErrors(message, status, errors);
  return {
    errors: commonErrors,
    type: TypeKeys.POLL_FOR_CREDIT_CHECK_FAILED,
  };
};

export type OnlineOrderActionTypes =
  | PollForCreditCheckAction
  | PollForCreditCheckFailedAction
  | SubmitOnlineOrderAction
  | SubmitOrderFulfilledAction
  | SubmitOrderFailedAction
  | PollForCreditCheckV2FulfilledAction
  | UpsertPersonBillingAccountAction
  | UpsertPersonBillingAccountFulfilledAction
  | UpsertPersonBillingAccountFailedAction;

export interface UpsertPersonBillingAccountAction extends CrudAction<TypeKeys.UPSERT_PERSON_BILLING_ACCOUNT> {
  request: UpsertPersonBillingAccountRequest;
  billingAccountId?: string;
  validationErrors?: CommonError[];
  successAction?: () => void;
}

export const upsertPersonBillingAccount = (
  request: UpsertPersonBillingAccountRequest,
  billingAccountId?: string,
  validationErrors?: CommonError[],
  successAction?: () => void
): UpsertPersonBillingAccountAction => {
  return {
    billingAccountId,
    request,
    successAction,
    validationErrors,
    id: generateActionId(),
    type: TypeKeys.UPSERT_PERSON_BILLING_ACCOUNT,
  };
};

export interface UpsertPersonBillingAccountFulfilledAction {
  isCreate: boolean;
  request: UpsertPersonBillingAccountRequest;
  response: PutBillingAccountResponse;
  successAction?: () => void;
  type: TypeKeys.UPSERT_PERSON_BILLING_ACCOUNT_FULFILLED;
}

export const upsertPersonBillingAccountFulfilled = (
  request: UpsertPersonBillingAccountRequest,
  response: PutBillingAccountResponse,
  isCreate: boolean,
  successAction?: () => void
): UpsertPersonBillingAccountFulfilledAction => ({
  request,
  response,
  isCreate,
  successAction,
  type: TypeKeys.UPSERT_PERSON_BILLING_ACCOUNT_FULFILLED,
});

export type UpsertPersonBillingAccountFailedAction = ErrorAction<TypeKeys.UPSERT_PERSON_BILLING_ACCOUNT_FAILED>;

export const upsertPersonBillingAccountFailed: ErrorActionCreator<TypeKeys.UPSERT_PERSON_BILLING_ACCOUNT_FAILED> = (
  message: string,
  status: number,
  errors?: GenericError[]
): UpsertPersonBillingAccountFailedAction => {
  return {
    errors: convertToCommonErrors(message, status, errors),
    type: TypeKeys.UPSERT_PERSON_BILLING_ACCOUNT_FAILED,
  };
};
